// src/hooks/useAccounting.js
// Hooks for Rozliczenia (Accounting) module
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { formatLocalDate } from '@/lib/dateUtils';

// =====================================================
// COST ELEMENTS
// =====================================================

/**
 * Get all cost elements for a branch
 */
export const useCostElements = (branchId, options = {}) => {
  return useQuery({
    queryKey: ['cost_elements', branchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cost_elements')
        .select('*')
        .eq('branch_id', branchId)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!branchId,
    ...options
  });
};

/**
 * Get child counts for shared cost elements in a branch
 * Returns Map<cost_element_id, child_count>
 * Uses SECURITY DEFINER function to bypass RLS so parents can see full counts
 */
export const useSharedCostChildCounts = (branchId, options = {}) => {
  return useQuery({
    queryKey: ['shared_cost_child_counts', branchId],
    queryFn: async () => {
      // Use SECURITY DEFINER function to get counts for all children,
      // not just those visible to the current user via RLS
      const { data, error } = await supabase
        .rpc('get_shared_cost_child_counts', { p_branch_id: branchId });

      if (error) throw error;

      // Convert array of {cost_element_id, child_count} to Map
      const counts = new Map();
      data?.forEach(row => {
        counts.set(row.cost_element_id, Number(row.child_count));
      });

      return counts;
    },
    enabled: !!branchId,
    ...options
  });
};

/**
 * Create a new cost element
 */
export const useCreateCostElement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (costElement) => {
      const { data, error } = await supabase
        .from('cost_elements')
        .insert(costElement)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cost_elements', variables.branch_id] });
      queryClient.invalidateQueries({ queryKey: ['billing_page_initial_data', variables.branch_id] });
      toast.success('Element kosztowy dodany');
    },
    onError: (error) => {
      logger.error('Create cost element error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Update a cost element
 */
export const useUpdateCostElement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }) => {
      const { data, error } = await supabase
        .from('cost_elements')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['cost_elements', data.branch_id] });
      queryClient.invalidateQueries({ queryKey: ['billing_page_initial_data', data.branch_id] });
      toast.success('Element kosztowy zaktualizowany');
    },
    onError: (error) => {
      logger.error('Update cost element error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Delete a cost element (and all its assignments)
 * Uses SECURITY DEFINER function to properly cascade through RLS
 */
export const useDeleteCostElement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, branchId }) => {
      const { error } = await supabase
        .rpc('delete_cost_element', { p_cost_element_id: id });

      if (error) throw error;
      return { id, branchId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['cost_elements', data.branchId] });
      queryClient.invalidateQueries({ queryKey: ['billing_page_initial_data', data.branchId] });
      queryClient.invalidateQueries({ queryKey: ['child_cost_assignments'] });
      queryClient.invalidateQueries({ queryKey: ['billing_children_data', data.branchId] });
      toast.success('Element kosztowy usunięty');
    },
    onError: (error) => {
      logger.error('Delete cost element error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

// =====================================================
// CHILD COST ASSIGNMENTS
// =====================================================

/**
 * Get cost assignments for children in a branch
 */
export const useChildCostAssignments = (branchId, options = {}) => {
  return useQuery({
    queryKey: ['child_cost_assignments', branchId],
    queryFn: async () => {
      // First get all children in the branch
      const { data: children, error: childrenError } = await supabase
        .from('children')
        .select('id')
        .eq('branch_id', branchId);

      if (childrenError) throw childrenError;
      if (!children || children.length === 0) return [];

      const childIds = children.map(c => c.id);

      const { data, error } = await supabase
        .from('child_cost_assignments')
        .select(`
          *,
          cost_elements (id, name, amount_cents, recurrence_type, cost_type, schedule_type, schedule_config)
        `)
        .in('child_id', childIds)
        .eq('is_active', true);

      if (error) throw error;
      return data || [];
    },
    enabled: !!branchId,
    ...options
  });
};

/**
 * Get cost assignments for a specific child
 */
export const useChildAssignments = (childId, options = {}) => {
  return useQuery({
    queryKey: ['child_cost_assignments', 'child', childId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('child_cost_assignments')
        .select(`
          *,
          cost_elements (*)
        `)
        .eq('child_id', childId)
        .eq('is_active', true);

      if (error) throw error;
      return data || [];
    },
    enabled: !!childId,
    ...options
  });
};

/**
 * Assign cost elements to children (bulk operation)
 */
export const useAssignCostElements = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ childIds, costElementId, customAmountCents = null }) => {
      // effective_from = first day of current month so billing uses full-month enrollment factor
      const firstOfMonth = new Date();
      firstOfMonth.setDate(1);
      const effectiveFrom = formatLocalDate(firstOfMonth);
      const assignments = childIds.map(childId => ({
        child_id: childId,
        cost_element_id: costElementId,
        custom_amount_cents: customAmountCents,
        effective_from: effectiveFrom,
        is_active: true
      }));

      const { data, error } = await supabase
        .from('child_cost_assignments')
        .upsert(assignments, {
          onConflict: 'child_id,cost_element_id',
          ignoreDuplicates: false
        })
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['child_cost_assignments'] });
      queryClient.invalidateQueries({ queryKey: ['billing_children_data'] });
      queryClient.invalidateQueries({ queryKey: ['children_with_costs'] });
      toast.success('Przypisania zaktualizowane');
    },
    onError: (error) => {
      logger.error('Assign cost elements error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Remove cost element assignment from children
 */
export const useRemoveCostAssignment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ childIds, costElementId }) => {
      const { error } = await supabase
        .from('child_cost_assignments')
        .delete()
        .in('child_id', childIds)
        .eq('cost_element_id', costElementId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['child_cost_assignments'] });
      queryClient.invalidateQueries({ queryKey: ['billing_children_data'] });
      queryClient.invalidateQueries({ queryKey: ['children_with_costs'] });
      toast.success('Przypisania usunięte');
    },
    onError: (error) => {
      logger.error('Remove cost assignment error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

// =====================================================
// BILLING PERIODS
// =====================================================

/**
 * Get billing periods for a branch
 */
export const useBillingPeriods = (branchId, options = {}) => {
  return useQuery({
    queryKey: ['billing_periods', branchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('billing_periods')
        .select('*')
        .eq('branch_id', branchId)
        .order('start_date', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!branchId,
    ...options
  });
};

/**
 * Create a new billing period
 */
export const useCreateBillingPeriod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (period) => {
      const { data, error } = await supabase
        .from('billing_periods')
        .insert(period)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['billing_periods', variables.branch_id] });
      queryClient.invalidateQueries({ queryKey: ['billing_page_initial_data', variables.branch_id] });
      toast.success('Okres rozliczeniowy utworzony');
    },
    onError: (error) => {
      logger.error('Create billing period error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Update a billing period
 */
export const useUpdateBillingPeriod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }) => {
      const { data, error } = await supabase
        .from('billing_periods')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['billing_periods', data.branch_id] });
      queryClient.invalidateQueries({ queryKey: ['billing_page_initial_data', data.branch_id] });
      toast.success('Okres rozliczeniowy zaktualizowany');
    },
    onError: (error) => {
      logger.error('Update billing period error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Calculate receipts for a billing period
 * Calls the calculate-billing-period edge function
 */
export const useCalculateBillingPeriod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (billingPeriodId) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calculate-billing-period`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ billing_period_id: billingPeriodId })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to calculate receipts');
      }

      return result;
    },
    onSuccess: (result, billingPeriodId) => {
      queryClient.invalidateQueries({ queryKey: ['billing_periods'] });
      queryClient.invalidateQueries({ queryKey: ['billing_page_initial_data'] });
      queryClient.invalidateQueries({ queryKey: ['parent_receipts'] });
      queryClient.invalidateQueries({ queryKey: ['period_receipts', billingPeriodId] });

      let message = `Wygenerowano ${result.receipts_created} rachunków`;
      if (result.receipts_skipped > 0) {
        message += ` (pominięto ${result.receipts_skipped} już wysłanych)`;
      }
      toast.success(message);

      if (result.had_manual_items_deleted) {
        toast.warning('Uwaga: Usunięto ręcznie dodane pozycje z rachunków roboczych');
      }
    },
    onError: (error) => {
      logger.error('Calculate billing period error', { message: error.message });
      toast.error(`Błąd kalkulacji: ${error.message}`);
    }
  });
};

/**
 * Close a billing period (prevent further changes)
 */
export const useCloseBillingPeriod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (billingPeriodId) => {
      const { data, error } = await supabase
        .from('billing_periods')
        .update({
          status: 'closed',
          closed_at: new Date().toISOString()
        })
        .eq('id', billingPeriodId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, billingPeriodId) => {
      queryClient.invalidateQueries({ queryKey: ['billing_periods'] });
      queryClient.invalidateQueries({ queryKey: ['billing_page_initial_data'] });
      toast.success('Okres rozliczeniowy zamknięty');
    },
    onError: (error) => {
      logger.error('Close billing period error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Reopen a billing period (admin only, use with caution)
 */
export const useReopenBillingPeriod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (billingPeriodId) => {
      const { data, error } = await supabase
        .from('billing_periods')
        .update({
          status: 'calculated',
          closed_at: null
        })
        .eq('id', billingPeriodId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, billingPeriodId) => {
      queryClient.invalidateQueries({ queryKey: ['billing_periods'] });
      queryClient.invalidateQueries({ queryKey: ['billing_page_initial_data'] });
      toast.success('Okres rozliczeniowy ponownie otwarty');
    },
    onError: (error) => {
      logger.error('Reopen billing period error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

// =====================================================
// PARENT RECEIPTS
// =====================================================

/**
 * Get receipts for a billing period
 */
export const usePeriodReceipts = (billingPeriodId, options = {}) => {
  return useQuery({
    queryKey: ['parent_receipts', 'period', billingPeriodId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('parent_receipts')
        .select(`
          *,
          children (id, first_name, last_name, group_id),
          app_users:parent_id (id, first_name, last_name, email)
        `)
        .eq('billing_period_id', billingPeriodId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!billingPeriodId,
    ...options
  });
};

/**
 * Get receipts for a parent (for parent's view)
 * Shows receipts for ALL children where this parent is in parent_ids,
 * not just where they're the primary parent (parent_id field)
 */
export const useMyReceipts = (parentId, options = {}) => {
  return useQuery({
    queryKey: ['parent_receipts', 'my', parentId],
    queryFn: async () => {
      // First, get all children where this parent is linked
      // Using filter with 'cs' (contains) operator for UUID array
      const { data: children, error: childrenError } = await supabase
        .from('children')
        .select('id')
        .filter('parent_ids', 'cs', `{${parentId}}`);

      if (childrenError) {
        logger.error('Error fetching children for parent', { parentId, error: childrenError });
        throw childrenError;
      }
      if (!children || children.length === 0) {
        logger.debug('No children found for parent', { parentId });
        return [];
      }

      const childIds = children.map(c => c.id);
      logger.debug('Found children for parent', { parentId, childIds });

      // Get receipts for those children, filtering to only visible statuses
      // (draft and calculated receipts shouldn't be shown to parents)
      // Include branch data for bank account info
      const { data, error } = await supabase
        .from('parent_receipts')
        .select(`
          *,
          children (id, first_name, last_name),
          billing_periods (
            id, name, start_date, end_date, status, branch_id,
            branches (id, name, display_name, billing_settings)
          ),
          receipt_line_items (*)
        `)
        .in('child_id', childIds)
        .in('status', ['sent', 'viewed', 'paid', 'overdue'])
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Error fetching receipts', { childIds, error });
        throw error;
      }

      logger.debug('Found receipts for parent', { parentId, count: data?.length });
      return data || [];
    },
    enabled: !!parentId,
    ...options
  });
};

/**
 * Get a single receipt with line items
 */
export const useReceipt = (receiptId, options = {}) => {
  return useQuery({
    queryKey: ['parent_receipts', receiptId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('parent_receipts')
        .select(`
          *,
          children (id, first_name, last_name, group_id),
          app_users:parent_id (id, first_name, last_name, email),
          billing_periods (id, name, start_date, end_date, branch_id),
          receipt_line_items (*)
        `)
        .eq('id', receiptId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!receiptId,
    ...options
  });
};

/**
 * Mark receipt as viewed by parent
 */
export const useMarkReceiptViewed = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (receiptId) => {
      const { data, error } = await supabase
        .from('parent_receipts')
        .update({
          viewed_at: new Date().toISOString(),
          status: 'viewed'
        })
        .eq('id', receiptId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['parent_receipts'] });
    }
  });
};

/**
 * Mark receipt as paid
 */
export const useMarkReceiptPaid = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ receiptId, paymentDate, paymentReference }) => {
      const { data, error } = await supabase
        .from('parent_receipts')
        .update({
          status: 'paid',
          paid_at: new Date().toISOString(),
          payment_date: paymentDate || formatLocalDate(new Date()),
          payment_reference: paymentReference || null,
          payment_method: 'admin_verified'
        })
        .eq('id', receiptId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['parent_receipts'] });
      toast.success('Rachunek oznaczony jako opłacony');
    },
    onError: (error) => {
      logger.error('Mark receipt paid error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Mark receipt as paid by parent (pending admin verification)
 * Parents can self-report payment which shows as "Zapłacone (DW)" - do weryfikacji
 */
export const useMarkReceiptPaidByParent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (receiptId) => {
      const { data, error } = await supabase
        .from('parent_receipts')
        .update({
          status: 'paid',
          paid_at: new Date().toISOString(),
          payment_date: formatLocalDate(new Date()),
          payment_method: 'parent_marked'
        })
        .eq('id', receiptId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['parent_receipts'] });
      toast.success('Rachunek oznaczony jako zapłacony. Oczekuje na weryfikację.');
    },
    onError: (error) => {
      logger.error('Mark receipt paid by parent error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

const RECEIPT_SEND_DELAY_MS = 350;

/**
 * Send receipts to parents (bulk operation) with tracking and progress.
 * Creates a send batch (parent_receipt_send_log), then calls send-receipt-email for each receipt.
 * @param {Object} params
 * @param {string[]} params.receiptIds - Receipt IDs to send
 * @param {function(number, number, object)} [params.onProgress] - (current, total, result) called after each send
 * @returns {Promise<{ results, successCount, errorCount, failedLogIds }>}
 */
export const useSendReceipts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ receiptIds, onProgress }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      if (!receiptIds?.length) {
        return { results: [], successCount: 0, errorCount: 0, failedLogIds: [] };
      }

      const sendBatchId = crypto.randomUUID();
      const { data: batchData, error: batchError } = await supabase.rpc('create_receipt_send_batch', {
        p_receipt_ids: receiptIds,
        p_send_batch_id: sendBatchId
      });

      if (batchError) {
        logger.error('Create receipt send batch error', { error: batchError });
        throw new Error(batchError.message || 'Nie udało się utworzyć batcha wysyłki');
      }

      const entries = batchData?.entries || [];
      if (entries.length === 0) {
        return { results: [], successCount: 0, errorCount: receiptIds.length, failedLogIds: [], send_batch_id: sendBatchId };
      }

      const results = [];
      let successCount = 0;
      let errorCount = 0;
      const failedLogIds = [];

      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        const receiptId = entry.receipt_id;
        const logId = entry.log_id;
        try {
          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-receipt-email`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
              },
              body: JSON.stringify({ receipt_id: receiptId, send_log_id: logId })
            }
          );

          const result = await response.json();
          const success = response.ok;
          if (success) successCount++; else { errorCount++; failedLogIds.push(logId); }
          logger[success ? 'debug' : 'error']('Send receipt', { receiptId, success, error: result.error });
          results.push({ receiptId, logId, success, result });
          if (typeof onProgress === 'function') {
            onProgress(i + 1, entries.length, { receiptId, logId, success, result });
          }
        } catch (err) {
          errorCount++;
          failedLogIds.push(logId);
          results.push({ receiptId, logId, success: false, error: err.message });
          if (typeof onProgress === 'function') {
            onProgress(i + 1, entries.length, { receiptId, logId, success: false, error: err.message });
          }
        }

        if (i < entries.length - 1) {
          await new Promise((r) => setTimeout(r, RECEIPT_SEND_DELAY_MS));
        }
      }

      return { results, successCount, errorCount, failedLogIds, send_batch_id: sendBatchId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['parent_receipts'] });
      if (data.errorCount === 0) {
        toast.success(`Wysłano ${data.successCount} rachunków`);
      } else {
        toast.warning(`Wysłano ${data.successCount}, błędy: ${data.errorCount}`);
      }
    },
    onError: (error) => {
      logger.error('Send receipts error', { message: error.message });
      toast.error(`Błąd wysyłania: ${error.message}`);
    }
  });
};

/**
 * Retry failed receipt sends by log IDs (from parent_receipt_send_log).
 * Calls retry-receipt-sends edge function.
 */
export const useRetryReceiptSends = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ branchId, sendLogIds, billingPeriodId }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      if (!branchId) throw new Error('branchId is required');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/retry-receipt-sends`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            branch_id: branchId,
            send_log_ids: sendLogIds || undefined,
            billing_period_id: billingPeriodId || undefined
          })
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Retry failed');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parent_receipts'] });
      toast.success('Ponowiono wysyłkę rachunków');
    },
    onError: (error) => {
      logger.error('Retry receipt sends error', { message: error.message });
      toast.error(`Błąd ponownej wysyłki: ${error.message}`);
    }
  });
};

/**
 * Generate PDF for a receipt
 * Calls the generate-receipt-pdf edge function
 * Returns the PDF URL which can be opened in a new tab
 */
export const useGenerateReceiptPDF = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (receiptId) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-receipt-pdf`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ receipt_id: receiptId })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to generate PDF');
      }

      return result;
    },
    onSuccess: (result, receiptId) => {
      queryClient.invalidateQueries({ queryKey: ['parent_receipts', receiptId] });
      // Open PDF in new tab
      if (result.pdf_url) {
        window.open(result.pdf_url, '_blank');
      }
    },
    onError: (error) => {
      logger.error('Generate PDF error', { message: error.message });
      toast.error(`Błąd generowania PDF: ${error.message}`);
    }
  });
};

// =====================================================
// RECEIPT PREFERENCES
// =====================================================

/**
 * Get receipt preferences for a parent
 */
export const useReceiptPreferences = (parentId, options = {}) => {
  return useQuery({
    queryKey: ['parent_receipt_preferences', parentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('parent_receipt_preferences')
        .select('*, children (id, first_name, last_name)')
        .eq('parent_id', parentId);

      if (error) throw error;
      return data || [];
    },
    enabled: !!parentId,
    ...options
  });
};

/**
 * Update receipt preference
 */
export const useUpdateReceiptPreference = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ parentId, childId, deliveryMethod, receiptEmail }) => {
      const { data, error } = await supabase
        .from('parent_receipt_preferences')
        .upsert({
          parent_id: parentId,
          child_id: childId,
          delivery_method: deliveryMethod,
          receipt_email: receiptEmail
        }, {
          onConflict: 'parent_id,child_id'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['parent_receipt_preferences', data.parent_id] });
      toast.success('Preferencje zapisane');
    },
    onError: (error) => {
      logger.error('Update receipt preference error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

// =====================================================
// COST ELEMENT OCCURRENCES (for custom schedules)
// =====================================================

/**
 * Get occurrences for a cost element in a billing period
 */
export const useCostElementOccurrences = (costElementId, billingPeriodId, options = {}) => {
  return useQuery({
    queryKey: ['cost_element_occurrences', costElementId, billingPeriodId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cost_element_occurrences')
        .select('*')
        .eq('cost_element_id', costElementId)
        .eq('billing_period_id', billingPeriodId)
        .order('occurrence_date', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!costElementId && !!billingPeriodId,
    ...options
  });
};

/**
 * Get all occurrences for a billing period (all cost elements)
 */
export const usePeriodOccurrences = (billingPeriodId, options = {}) => {
  return useQuery({
    queryKey: ['cost_element_occurrences', 'period', billingPeriodId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cost_element_occurrences')
        .select(`
          *,
          cost_elements (id, name, amount_cents, cost_type)
        `)
        .eq('billing_period_id', billingPeriodId)
        .order('occurrence_date', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!billingPeriodId,
    ...options
  });
};

/**
 * Add occurrence date for a cost element
 */
export const useAddCostElementOccurrence = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ costElementId, billingPeriodId, occurrenceDate, notes }) => {
      const { data, error } = await supabase
        .from('cost_element_occurrences')
        .insert({
          cost_element_id: costElementId,
          billing_period_id: billingPeriodId,
          occurrence_date: occurrenceDate,
          notes
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['cost_element_occurrences'] });
      toast.success('Data dodana');
    },
    onError: (error) => {
      logger.error('Add occurrence error', { message: error.message, code: error.code });
      if (error.code === '23505') {
        toast.error('Ta data już istnieje');
      } else {
        toast.error(`Błąd: ${error.message}`);
      }
    }
  });
};

/**
 * Remove occurrence date
 */
export const useRemoveCostElementOccurrence = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (occurrenceId) => {
      const { error } = await supabase
        .from('cost_element_occurrences')
        .delete()
        .eq('id', occurrenceId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost_element_occurrences'] });
      toast.success('Data usunięta');
    },
    onError: (error) => {
      logger.error('Remove occurrence error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

// =====================================================
// PARENT ENROLLMENT FUNCTIONS
// =====================================================

/**
 * Get enrollable cost elements for a branch (for parents)
 * Includes current enrollment count for capacity display
 */
export const useEnrollableCostElements = (branchId, options = {}) => {
  return useQuery({
    queryKey: ['enrollable_cost_elements', branchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cost_elements')
        .select('*')
        .eq('branch_id', branchId)
        .eq('is_active', true)
        .eq('is_enrollable', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      if (!data || data.length === 0) return [];

      // For each cost element with capacity, fetch current enrollment count
      // Uses RPC function with SECURITY DEFINER to bypass RLS and get accurate count
      const elementsWithCounts = await Promise.all(
        data.map(async (element) => {
          if (element.max_capacity) {
            const { data: count, error: rpcError } = await supabase
              .rpc('get_cost_element_enrollment_count', { element_id: element.id });

            if (rpcError) {
              logger.error('Error getting enrollment count', { message: rpcError.message });
              return { ...element, current_enrollment_count: 0 };
            }

            return { ...element, current_enrollment_count: count || 0 };
          }
          return element;
        })
      );

      return elementsWithCounts;
    },
    enabled: !!branchId,
    ...options
  });
};

/**
 * Get child's current enrollments (for a specific child)
 * Includes enrollments with pending unenroll dates so user can see and cancel them
 */
export const useChildEnrollments = (childId, options = {}) => {
  return useQuery({
    queryKey: ['child_enrollments', childId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('child_cost_assignments')
        .select(`
          *,
          cost_elements (id, name, description, amount_cents, is_enrollable, max_capacity)
        `)
        .eq('child_id', childId)
        .eq('is_active', true);

      if (error) throw error;
      return data || [];
    },
    enabled: !!childId,
    ...options
  });
};

/**
 * Get enrolled activities for multiple children (for calendar display)
 * Returns activities with schedule info for date calculation
 */
export const useChildrenEnrolledActivities = (childIds = [], options = {}) => {
  return useQuery({
    queryKey: ['children_enrolled_activities', childIds],
    queryFn: async () => {
      if (!childIds.length) return [];

      const { data, error } = await supabase
        .from('child_cost_assignments')
        .select(`
          id,
          child_id,
          cost_element_id,
          effective_until,
          cost_elements (
            id,
            name,
            schedule_type,
            schedule_config,
            is_enrollable,
            is_active
          )
        `)
        .in('child_id', childIds)
        .eq('is_active', true);

      if (error) throw error;

      // Filter to only enrollable, active cost elements
      return (data || []).filter(assignment =>
        assignment.cost_elements?.is_enrollable &&
        assignment.cost_elements?.is_active
      );
    },
    enabled: childIds.length > 0,
    ...options
  });
};

/**
 * Enroll a child in a cost element (parent action)
 */
export const useEnrollChild = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ childId, costElementId, userId }) => {
      // Check if already enrolled
      const { data: existing } = await supabase
        .from('child_cost_assignments')
        .select('id')
        .eq('child_id', childId)
        .eq('cost_element_id', costElementId)
        .eq('is_active', true)
        .maybeSingle();

      if (existing) {
        throw new Error('Dziecko jest już zapisane');
      }

      // Check capacity if applicable
      const { data: costElement } = await supabase
        .from('cost_elements')
        .select('max_capacity')
        .eq('id', costElementId)
        .single();

      if (costElement?.max_capacity) {
        // Count active enrollments (no effective_until or effective_until in the future)
        const today = formatLocalDate(new Date());
        const { count } = await supabase
          .from('child_cost_assignments')
          .select('*', { count: 'exact', head: true })
          .eq('cost_element_id', costElementId)
          .eq('is_active', true)
          .or(`effective_until.is.null,effective_until.gte.${today}`);

        if (count >= costElement.max_capacity) {
          throw new Error('Brak wolnych miejsc');
        }
      }

      // effective_from = first day of current month so billing uses full-month enrollment factor
      const firstOfMonth = new Date();
      firstOfMonth.setDate(1);
      const effectiveFrom = formatLocalDate(firstOfMonth);
      const { data, error } = await supabase
        .from('child_cost_assignments')
        .insert({
          child_id: childId,
          cost_element_id: costElementId,
          effective_from: effectiveFrom,
          is_active: true,
          enrolled_by: userId,
          enrolled_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['child_enrollments', variables.childId] });
      queryClient.invalidateQueries({ queryKey: ['enrollable_cost_elements'] });
      queryClient.invalidateQueries({ queryKey: ['children_with_costs'] });
      queryClient.invalidateQueries({ queryKey: ['billing_children_data'] });
      toast.success('Dziecko zostało zapisane');
    },
    onError: (error) => {
      logger.error('Enroll error', { message: error.message });
      toast.error(error.message || 'Błąd zapisu');
    }
  });
};

/**
 * Unenroll a child from a cost element (immediate - sets effective_until to today)
 * Past occurrences within the billing period will still be calculated when billing runs
 */
export const useUnenrollChild = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ childId, costElementId, branchId }) => {
      // Immediate unenrollment - effective_until is set to today
      // Past occurrences within the billing period will still be calculated
      const effectiveDate = formatLocalDate(new Date());

      const { data, error } = await supabase
        .from('child_cost_assignments')
        .update({
          effective_until: effectiveDate,
          updated_at: new Date().toISOString()
        })
        .eq('child_id', childId)
        .eq('cost_element_id', costElementId)
        .eq('is_active', true)
        .select()
        .single();

      if (error) throw error;

      // Check if this is a capacity-limited activity and notify other parents
      try {
        // Get the cost element details
        const { data: costElement } = await supabase
          .from('cost_elements')
          .select('id, name, max_capacity, is_enrollable')
          .eq('id', costElementId)
          .single();

        // Only notify if this is an enrollable activity with limited capacity
        if (costElement?.is_enrollable && costElement?.max_capacity > 0) {
          // Check current enrollment count (excluding this unenrolling child)
          // An active enrollment has is_active=true and either no effective_until or effective_until in the future
          const { count } = await supabase
            .from('child_cost_assignments')
            .select('id', { count: 'exact', head: true })
            .eq('cost_element_id', costElementId)
            .eq('is_active', true)
            .or(`effective_until.is.null,effective_until.gt.${effectiveDate}`)
            .neq('child_id', childId);

          const enrollmentCount = count || 0;

          // If activity was at capacity (this unenroll frees a spot), notify group parents
          if (enrollmentCount >= costElement.max_capacity - 1) {
            // Get the child's group and then all parents in that group
            const { data: child } = await supabase
              .from('children')
              .select('id, first_name, group_id')
              .eq('id', childId)
              .single();

            if (child?.group_id) {
              // Get all children in the group with their parents
              const { data: groupChildren } = await supabase
                .from('children')
                .select('parent_ids')
                .eq('group_id', child.group_id)
                .eq('status', 'active')
                .neq('id', childId); // Exclude the unenrolling child

              // Collect unique parent IDs
              const parentIds = new Set();
              groupChildren?.forEach(c => {
                c.parent_ids?.forEach(pid => parentIds.add(pid));
              });

              if (parentIds.size > 0) {
                const notifications = Array.from(parentIds).map(parentId => ({
                  user_id: parentId,
                  type: 'seat_available',
                  title: 'Zwolniło się miejsce',
                  message: `Zwolniło się miejsce na zajęciach: ${costElement.name}`,
                  priority: 'normal',
                  read: false,
                  metadata: {
                    cost_element_id: costElementId,
                    cost_element_name: costElement.name,
                    available_from: effectiveDate,
                    group_id: child.group_id
                  }
                }));

                await supabase.from('notifications').insert(notifications);
                logger.debug('Seat available notifications created', { count: notifications.length });
              }
            }
          }
        }
      } catch (notifError) {
        // Non-critical - log but don't fail
        logger.error('Failed to create seat available notifications:', notifError);
      }

      return { ...data, effectiveDate };
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['child_enrollments', variables.childId] });
      queryClient.invalidateQueries({ queryKey: ['children_with_costs'] });
      queryClient.invalidateQueries({ queryKey: ['billing_children_data'] });
      toast.success('Wypisano z zajęć. Dotychczasowe koszty zostaną rozliczone.');
    },
    onError: (error) => {
      logger.error('Unenroll error', { message: error.message });
      toast.error(error.message || 'Błąd wypisywania');
    }
  });
};

/**
 * Re-enroll a child (undo recent unenrollment by clearing effective_until)
 * Only works if the assignment is still active (is_active=true)
 */
export const useCancelUnenrollment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ childId, costElementId }) => {
      const { data, error } = await supabase
        .from('child_cost_assignments')
        .update({
          effective_until: null,
          updated_at: new Date().toISOString()
        })
        .eq('child_id', childId)
        .eq('cost_element_id', costElementId)
        .eq('is_active', true)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['child_enrollments', variables.childId] });
      queryClient.invalidateQueries({ queryKey: ['children_with_costs'] });
      queryClient.invalidateQueries({ queryKey: ['billing_children_data'] });
      toast.success('Przywrócono zapis na zajęcia');
    },
    onError: (error) => {
      logger.error('Cancel unenroll error', { message: error.message });
      toast.error(error.message || 'Błąd przywracania zapisu');
    }
  });
};

// =====================================================
// UTILITY FUNCTIONS
// =====================================================

/**
 * Get Polish day name
 */
export const getDayName = (dayNumber) => {
  const days = ['', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela'];
  return days[dayNumber] || '';
};

/**
 * Get short Polish day name
 */
export const getDayShortName = (dayNumber) => {
  const days = ['', 'Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'];
  return days[dayNumber] || '';
};

/**
 * Get ordinal week label in Polish
 */
export const getWeekOrdinalName = (weekNumber) => {
  const weeks = {
    1: '1.',
    2: '2.',
    3: '3.',
    4: '4.',
    '-1': 'Ost.'
  };
  return weeks[weekNumber] || '';
};

/**
 * Get the Nth occurrence of a weekday in a given month
 * @param {number} year - The year
 * @param {number} month - The month (0-11)
 * @param {number} dayOfWeek - Day of week (1=Mon, 2=Tue, ... 7=Sun)
 * @param {number} weekNumber - Which occurrence (1=first, 2=second, ... -1=last)
 * @returns {Date|null} The date, or null if it doesn't exist
 */
export const getNthWeekdayOfMonth = (year, month, dayOfWeek, weekNumber) => {
  // Convert our day format (1=Mon...7=Sun) to JS format (0=Sun...6=Sat)
  const jsDay = dayOfWeek === 7 ? 0 : dayOfWeek;

  if (weekNumber === -1) {
    // Last occurrence: start from end of month and go backwards
    const lastDay = new Date(year, month + 1, 0);
    let current = lastDay;

    while (current.getMonth() === month) {
      if (current.getDay() === jsDay) {
        return current;
      }
      current = new Date(current);
      current.setDate(current.getDate() - 1);
    }
    return null;
  } else {
    // Nth occurrence: start from beginning and count
    const firstDay = new Date(year, month, 1);
    let current = firstDay;
    let count = 0;

    while (current.getMonth() === month) {
      if (current.getDay() === jsDay) {
        count++;
        if (count === weekNumber) {
          return current;
        }
      }
      current = new Date(current);
      current.setDate(current.getDate() + 1);
    }
    return null; // Requested week doesn't exist (e.g., 5th Monday)
  }
};

/**
 * Format schedule config to human readable text
 */
export const formatScheduleConfig = (scheduleType, scheduleConfig) => {
  if (!scheduleConfig) return '';

  switch (scheduleType) {
    case 'weekly':
      const days = scheduleConfig.days || [];
      return days.map(d => getDayShortName(d)).join(', ');
    case 'monthly':
      const dates = scheduleConfig.dates || [];
      return dates.map(d => `${d}.`).join(', ');
    case 'monthly_weekday':
      const patterns = scheduleConfig.patterns || [];
      if (patterns.length === 0) return '';
      return patterns.map(p => `${getWeekOrdinalName(p.week)} ${getDayShortName(p.day)}`).join(', ');
    case 'custom':
      return 'Daty ręczne';
    default:
      return 'Codziennie';
  }
};

/**
 * Format amount from cents to PLN string
 */
export const formatAmount = (cents) => {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return '0,00 zł';
  const zloty = cents / 100;
  return zloty.toLocaleString('pl-PL', {
    style: 'currency',
    currency: 'PLN',
    minimumFractionDigits: 2
  });
};

/**
 * Parse PLN string to cents
 */
export const parseAmount = (value) => {
  if (!value) return 0;
  // Remove currency symbol and spaces
  const cleaned = value.toString().replace(/[^\d,.-]/g, '').replace(',', '.');
  const parsed = parseFloat(cleaned);
  if (isNaN(parsed)) return 0;
  return Math.round(parsed * 100);
};

// =====================================================
// PRICING AND SCHEDULE HELPERS
// =====================================================

/**
 * Count occurrences in a month based on schedule configuration
 * @param {string} scheduleType - 'weekly', 'monthly', 'custom', or 'all_days'
 * @param {object} scheduleConfig - Configuration with days/dates
 * @param {number} year - Year (optional, defaults to current)
 * @param {number} month - Month 0-11 (optional, defaults to current)
 * @returns {number} Estimated number of occurrences in the month
 */
export const countMonthlyOccurrences = (scheduleType, scheduleConfig, year, month) => {
  const now = new Date();
  const targetYear = year ?? now.getFullYear();
  const targetMonth = month ?? now.getMonth();

  // Get first and last day of target month
  const firstDay = new Date(targetYear, targetMonth, 1);
  const lastDay = new Date(targetYear, targetMonth + 1, 0);
  const daysInMonth = lastDay.getDate();

  switch (scheduleType) {
    case 'weekly': {
      // scheduleConfig.days is array of day numbers (1=Mon, 2=Tue, ... 7=Sun)
      const scheduledDays = scheduleConfig?.days || [];
      if (scheduledDays.length === 0) return 0;

      let count = 0;
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(targetYear, targetMonth, day);
        // JS getDay: 0=Sun, 1=Mon, ... 6=Sat
        // Our format: 1=Mon, 2=Tue, ... 7=Sun
        const jsDay = date.getDay();
        const ourDay = jsDay === 0 ? 7 : jsDay;

        if (scheduledDays.includes(ourDay)) {
          count++;
        }
      }
      return count;
    }

    case 'monthly': {
      // scheduleConfig.dates is array of day numbers in month (e.g., [1, 15])
      const scheduledDates = scheduleConfig?.dates || [];
      // Only count dates that exist in this month
      return scheduledDates.filter(d => d <= daysInMonth).length;
    }

    case 'monthly_weekday': {
      // scheduleConfig.patterns is array of { week, day } objects
      // week: 1-4 for 1st-4th, -1 for last
      // day: 1-7 for Mon-Sun
      const patterns = scheduleConfig?.patterns || [];
      if (patterns.length === 0) return 0;

      let count = 0;
      for (const pattern of patterns) {
        const date = getNthWeekdayOfMonth(targetYear, targetMonth, pattern.day, pattern.week);
        if (date && date.getMonth() === targetMonth) {
          count++;
        }
      }
      return count;
    }

    case 'all_days': {
      // All weekdays (Mon-Fri) in the month
      let count = 0;
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(targetYear, targetMonth, day);
        const dayOfWeek = date.getDay();
        // Mon-Fri = 1-5
        if (dayOfWeek >= 1 && dayOfWeek <= 5) {
          count++;
        }
      }
      return count;
    }

    case 'custom':
      // Custom dates are tracked separately in cost_element_occurrences table
      // Return 0 here - actual count comes from the occurrences query
      return 0;

    default:
      // Default to 4 occurrences per month (once a week average)
      return 4;
  }
};

/**
 * Get all activity occurrence dates within a date range
 * @param {string} scheduleType - 'weekly', 'monthly', 'monthly_weekday', 'custom', or 'all_days'
 * @param {object} scheduleConfig - Configuration with days/dates/patterns
 * @param {Date} startDate - Range start
 * @param {Date} endDate - Range end
 * @returns {string[]} Array of date strings in 'yyyy-MM-dd' format
 */
export const getActivityDatesInRange = (scheduleType, scheduleConfig, startDate, endDate) => {
  const dates = [];

  // For monthly_weekday, we need to process month by month
  if (scheduleType === 'monthly_weekday') {
    const patterns = scheduleConfig?.patterns || [];
    if (patterns.length === 0) return [];

    // Iterate through each month in the range
    const currentMonth = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const endMonth = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0);

    while (currentMonth <= endMonth) {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();

      for (const pattern of patterns) {
        const date = getNthWeekdayOfMonth(year, month, pattern.day, pattern.week);
        if (date && date >= startDate && date <= endDate) {
          const y = date.getFullYear();
          const m = String(date.getMonth() + 1).padStart(2, '0');
          const d = String(date.getDate()).padStart(2, '0');
          dates.push(`${y}-${m}-${d}`);
        }
      }

      // Move to next month
      currentMonth.setMonth(currentMonth.getMonth() + 1);
    }

    return dates.sort();
  }

  // For other schedule types, iterate day by day
  const current = new Date(startDate);

  while (current <= endDate) {
    const jsDay = current.getDay(); // 0=Sun, 1=Mon, ... 6=Sat
    const ourDay = jsDay === 0 ? 7 : jsDay; // Convert to 1=Mon, ... 7=Sun
    const dayOfMonth = current.getDate();

    let isActivityDay = false;

    switch (scheduleType) {
      case 'weekly':
        // Check if current day is in scheduled days
        isActivityDay = (scheduleConfig?.days || []).includes(ourDay);
        break;

      case 'monthly':
        // Check if current date is in scheduled dates
        isActivityDay = (scheduleConfig?.dates || []).includes(dayOfMonth);
        break;

      case 'all_days':
        // All weekdays (Mon-Fri)
        isActivityDay = jsDay >= 1 && jsDay <= 5;
        break;

      case 'custom':
        // Custom dates handled separately via cost_element_occurrences table
        isActivityDay = false;
        break;

      default:
        isActivityDay = false;
    }

    if (isActivityDay) {
      // Format as yyyy-MM-dd
      const year = current.getFullYear();
      const month = String(current.getMonth() + 1).padStart(2, '0');
      const day = String(current.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
    }

    current.setDate(current.getDate() + 1);
  }

  return dates;
};

/**
 * Calculate estimated monthly cost for a cost element
 * @param {object} costElement - The cost element object
 * @param {number} year - Optional year
 * @param {number} month - Optional month (0-11)
 * @param {number} sharedChildCount - For shared costs: number of children this cost is divided among
 * @returns {object} { unitCost, estimatedMonthlyCost, occurrences, isMonthlyFlat, priceLabel, isShared, childCount }
 */
export const calculateMonthlyEstimate = (costElement, year, month, sharedChildCount = 1) => {
  const baseCostCents = costElement.amount_cents || 0;
  const isMonthlyFlat = costElement.recurrence_type === 'spread';
  const isShared = costElement.cost_type === 'shared';

  // Per-event from Dziennik (Dodatkowe pola): quantity is variable, no monthly estimate
  const isDziennikFieldSource =
    costElement.calculation_method === 'per_event' && costElement.event_source === 'dziennik_field';
  if (isDziennikFieldSource) {
    const childCount = isShared ? Math.max(1, sharedChildCount) : 1;
    const unitCostCents = isShared ? Math.round(baseCostCents / childCount) : baseCostCents;
    const unitLabel = '/jedn.';
    let priceLabel = formatAmount(unitCostCents) + unitLabel;
    if (isShared && childCount > 1) {
      priceLabel = `${formatAmount(baseCostCents)} ÷ ${childCount} = ${formatAmount(unitCostCents)}${unitLabel}`;
    } else if (isShared) {
      priceLabel = formatAmount(unitCostCents) + unitLabel + ' (wspólny)';
    }
    return {
      unitCost: unitCostCents,
      baseCost: baseCostCents,
      estimatedMonthlyCost: 0,
      occurrences: null,
      isMonthlyFlat: false,
      isShared,
      childCount,
      priceLabel,
      isVariableQuantity: true
    };
  }

  // For shared costs, divide by number of children
  const childCount = isShared ? Math.max(1, sharedChildCount) : 1;
  const unitCostCents = isShared ? Math.round(baseCostCents / childCount) : baseCostCents;

  if (isMonthlyFlat) {
    // Monthly flat rate - no calculation needed
    let priceLabel = formatAmount(unitCostCents) + '/mies.';
    if (isShared && childCount > 1) {
      priceLabel = `${formatAmount(baseCostCents)} ÷ ${childCount} = ${formatAmount(unitCostCents)}/mies.`;
    } else if (isShared) {
      priceLabel = formatAmount(unitCostCents) + '/mies. (wspólny)';
    }

    return {
      unitCost: unitCostCents,
      baseCost: baseCostCents,
      estimatedMonthlyCost: unitCostCents,
      occurrences: null,
      isMonthlyFlat: true,
      isShared,
      childCount,
      priceLabel,
      isVariableQuantity: false
    };
  }

  // Per-occurrence pricing
  const occurrences = countMonthlyOccurrences(
    costElement.schedule_type,
    costElement.schedule_config,
    year,
    month
  );

  const estimatedMonthlyCost = unitCostCents * occurrences;

  // Build price label
  let unitLabel = '/zajęcia';
  if (costElement.schedule_type === 'all_days') {
    unitLabel = '/dzień';
  }

  let priceLabel;
  if (isShared && childCount > 1) {
    priceLabel = `${formatAmount(baseCostCents)} ÷ ${childCount} = ${formatAmount(unitCostCents)}${unitLabel}`;
  } else if (isShared) {
    priceLabel = formatAmount(unitCostCents) + unitLabel + ' (wspólny)';
  } else {
    priceLabel = formatAmount(unitCostCents) + unitLabel;
  }

  if (occurrences > 0) {
    priceLabel += ` (~${formatAmount(estimatedMonthlyCost)}/mies.)`;
  }

  return {
    unitCost: unitCostCents,
    baseCost: baseCostCents,
    estimatedMonthlyCost,
    occurrences,
    isMonthlyFlat: false,
    isShared,
    childCount,
    priceLabel,
    isVariableQuantity: false
  };
};

/**
 * Build a map of shared cost element IDs to their child counts
 * @param {Array} allAssignments - All cost assignments across all children
 * @returns {Map<string, number>} Map of cost_element_id -> child count
 */
export const buildSharedCostChildCounts = (allAssignments) => {
  const counts = new Map();

  for (const assignment of allAssignments || []) {
    const costElement = assignment.cost_elements;
    if (costElement?.cost_type === 'shared') {
      const id = assignment.cost_element_id;
      counts.set(id, (counts.get(id) || 0) + 1);
    }
  }

  return counts;
};

/**
 * Get schedule description for display
 * @param {object} costElement - The cost element
 * @returns {string} Human-readable schedule description
 */
export const getScheduleDescription = (costElement) => {
  if (costElement.recurrence_type === 'spread') {
    return 'Opłata miesięczna';
  }

  const scheduleText = formatScheduleConfig(
    costElement.schedule_type,
    costElement.schedule_config
  );

  if (scheduleText) {
    return scheduleText;
  }

  if (costElement.schedule_type === 'all_days') {
    return 'Codziennie (Pn-Pt)';
  }

  if (costElement.schedule_type === 'custom') {
    return 'Terminy ustalane indywidualnie';
  }

  if (costElement.schedule_type === 'monthly_weekday') {
    return 'Określone dni miesiąca';
  }

  return '';
};

// =====================================================
// RECEIPT LINE ITEM MANAGEMENT (Manual Adjustments)
// =====================================================

/**
 * Get line items for a receipt
 */
export const useReceiptLineItems = (receiptId, options = {}) => {
  return useQuery({
    queryKey: ['receipt_line_items', receiptId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('receipt_line_items')
        .select(`
          *,
          cost_elements (id, name, calculation_method),
          added_by_user:added_by (first_name, last_name)
        `)
        .eq('receipt_id', receiptId)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!receiptId,
    ...options
  });
};

/**
 * Add a manual line item to a receipt
 */
export const useAddLineItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ receiptId, description, amountCents, quantity, vatRate, netAmountCents, vatAmountCents, notes, userId }) => {
      // Get current max display_order
      const { data: existingItems } = await supabase
        .from('receipt_line_items')
        .select('display_order')
        .eq('receipt_id', receiptId)
        .order('display_order', { ascending: false })
        .limit(1);

      const nextOrder = (existingItems?.[0]?.display_order || 0) + 1;

      const { data, error } = await supabase
        .from('receipt_line_items')
        .insert({
          receipt_id: receiptId,
          description,
          base_amount_cents: amountCents,
          calculated_amount_cents: amountCents,
          net_amount_cents: netAmountCents ?? amountCents,
          vat_rate: vatRate ?? 0,
          vat_amount_cents: vatAmountCents ?? 0,
          quantity: quantity ?? 1,
          display_order: nextOrder,
          is_manual: true,
          added_by: userId,
          notes
        })
        .select()
        .single();

      if (error) throw error;
      return { lineItem: data, receiptId };
    },
    onSuccess: async ({ lineItem, receiptId }) => {
      // Recalculate receipt totals
      await recalculateReceiptTotals(receiptId);

      queryClient.invalidateQueries({ queryKey: ['receipt_line_items', receiptId] });
      queryClient.invalidateQueries({ queryKey: ['parent_receipts'] });
      toast.success('Pozycja dodana');
    },
    onError: (error) => {
      logger.error('Add line item error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Update a line item
 */
export const useUpdateLineItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, receiptId, description, amountCents, quantity, vatRate, netAmountCents, vatAmountCents, notes }) => {
      const updateData = {
        description,
        base_amount_cents: amountCents,
        calculated_amount_cents: amountCents,
        notes
      };

      // Add optional fields if provided
      if (quantity !== undefined) updateData.quantity = quantity;
      if (vatRate !== undefined) updateData.vat_rate = vatRate;
      if (netAmountCents !== undefined) updateData.net_amount_cents = netAmountCents;
      if (vatAmountCents !== undefined) updateData.vat_amount_cents = vatAmountCents;

      // If VAT fields not provided, use amountCents as net
      if (netAmountCents === undefined) updateData.net_amount_cents = amountCents;

      const { data, error } = await supabase
        .from('receipt_line_items')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { lineItem: data, receiptId };
    },
    onSuccess: async ({ lineItem, receiptId }) => {
      // Recalculate receipt totals
      await recalculateReceiptTotals(receiptId);

      queryClient.invalidateQueries({ queryKey: ['receipt_line_items', receiptId] });
      queryClient.invalidateQueries({ queryKey: ['parent_receipts'] });
      toast.success('Pozycja zaktualizowana');
    },
    onError: (error) => {
      logger.error('Update line item error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Delete a line item
 */
export const useDeleteLineItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, receiptId }) => {
      const { error } = await supabase
        .from('receipt_line_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { receiptId };
    },
    onSuccess: async ({ receiptId }) => {
      // Recalculate receipt totals
      await recalculateReceiptTotals(receiptId);

      queryClient.invalidateQueries({ queryKey: ['receipt_line_items', receiptId] });
      queryClient.invalidateQueries({ queryKey: ['parent_receipts'] });
      toast.success('Pozycja usunięta');
    },
    onError: (error) => {
      logger.error('Delete line item error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Helper function to recalculate receipt totals after line item changes
 */
async function recalculateReceiptTotals(receiptId) {
  // Get all line items for this receipt
  const { data: lineItems, error: itemsError } = await supabase
    .from('receipt_line_items')
    .select('calculated_amount_cents, net_amount_cents, vat_amount_cents')
    .eq('receipt_id', receiptId);

  if (itemsError) {
    logger.error('Error fetching line items for recalculation', { message: itemsError.message });
    return;
  }

  // Calculate totals
  const totalAmount = lineItems.reduce((sum, item) => sum + (item.calculated_amount_cents || 0), 0);
  const netTotal = lineItems.reduce((sum, item) => sum + (item.net_amount_cents || 0), 0);
  const vatTotal = lineItems.reduce((sum, item) => sum + (item.vat_amount_cents || 0), 0);

  // Update receipt
  const { error: updateError } = await supabase
    .from('parent_receipts')
    .update({
      total_amount_cents: totalAmount,
      net_total_cents: netTotal,
      vat_total_cents: vatTotal,
      updated_at: new Date().toISOString()
    })
    .eq('id', receiptId);

  if (updateError) {
    logger.error('Error updating receipt totals', { message: updateError.message });
  }
}

/**
 * Mark a receipt as corrected (for audit trail)
 */
export const useMarkReceiptCorrected = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ receiptId, reason, userId }) => {
      const { data, error } = await supabase
        .from('parent_receipts')
        .update({
          corrected_at: new Date().toISOString(),
          corrected_by: userId,
          correction_reason: reason,
          // Clear the PDF URL so it regenerates
          pdf_url: null
        })
        .eq('id', receiptId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['parent_receipts'] });
      toast.success('Korekta zapisana');
    },
    onError: (error) => {
      logger.error('Mark receipt corrected error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Create a corrected receipt (Option A flow)
 * 1. Marks original receipt as 'corrected' FIRST (to free up the unique constraint)
 * 2. Creates NEW receipt with new number
 * 3. Copies line items to new receipt
 * 4. Creates credit note for original receipt
 * 5. Links the receipts together
 */
export const useCreateCorrectedReceipt = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ originalReceipt, lineItems, branchId, reason, userId }) => {
      // Step 1: Mark original receipt as 'corrected' FIRST
      // This frees up the unique constraint so we can create the new receipt
      const { error: updateError } = await supabase
        .from('parent_receipts')
        .update({
          status: 'corrected',
          corrected_at: new Date().toISOString(),
          corrected_by: userId,
          correction_reason: reason
        })
        .eq('id', originalReceipt.id);

      if (updateError) throw updateError;

      // Step 2: Generate new receipt number using the same branch prefix letter
      // Extract the letter prefix from original receipt (e.g., "A" from "A/2025/12/050")
      const originalParts = originalReceipt.receipt_number.split('/');
      const branchPrefix = originalParts[0]; // e.g., "A"

      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');

      // Get the highest sequence number for this branch prefix (across all years/months)
      const { data: existingReceipts, error: seqError } = await supabase
        .from('parent_receipts')
        .select('receipt_number')
        .like('receipt_number', `${branchPrefix}/%`)
        .order('receipt_number', { ascending: false })
        .limit(10);

      if (seqError) throw seqError;

      // Find the highest sequence number
      let nextSeq = 1;
      if (existingReceipts && existingReceipts.length > 0) {
        const seqNumbers = existingReceipts.map(r => {
          const parts = r.receipt_number.split('/');
          return parseInt(parts[3], 10) || 0;
        });
        nextSeq = Math.max(...seqNumbers) + 1;
      }

      const newReceiptNumber = `${branchPrefix}/${year}/${month}/${String(nextSeq).padStart(3, '0')}`;

      // Same parent(s) as original so corrected receipt is sent to all when admin sends
      const parentIds = (originalReceipt.parent_ids && originalReceipt.parent_ids.length > 0)
        ? originalReceipt.parent_ids
        : (originalReceipt.parent_id ? [originalReceipt.parent_id] : []);

      // Step 3: Create the new receipt
      const { data: newReceipt, error: createError } = await supabase
        .from('parent_receipts')
        .insert({
          billing_period_id: originalReceipt.billing_period_id,
          child_id: originalReceipt.child_id,
          parent_id: originalReceipt.parent_id,
          parent_ids: parentIds,
          receipt_number: newReceiptNumber,
          status: 'calculated',
          total_amount_cents: originalReceipt.total_amount_cents,
          net_total_cents: originalReceipt.net_total_cents,
          vat_total_cents: originalReceipt.vat_total_cents,
          due_date: originalReceipt.due_date,
          total_days: originalReceipt.total_days,
          attended_days: originalReceipt.attended_days,
          planned_absence_days: originalReceipt.planned_absence_days,
          unplanned_absence_days: originalReceipt.unplanned_absence_days,
          replaces_receipt_id: originalReceipt.id
        })
        .select()
        .single();

      if (createError) throw createError;

      // Step 4: Copy line items to new receipt
      if (lineItems && lineItems.length > 0) {
        const newLineItems = lineItems.map((item, index) => ({
          receipt_id: newReceipt.id,
          cost_element_id: item.cost_element_id,
          description: item.description,
          base_amount_cents: item.base_amount_cents,
          calculated_amount_cents: item.calculated_amount_cents,
          quantity: item.quantity,
          vat_rate: item.vat_rate || 0,
          vat_amount_cents: item.vat_amount_cents || 0,
          net_amount_cents: item.net_amount_cents || item.calculated_amount_cents,
          notes: item.notes,
          display_order: index + 1,
          is_manual: item.is_manual || false
        }));

        const { error: itemsError } = await supabase
          .from('receipt_line_items')
          .insert(newLineItems);

        if (itemsError) throw itemsError;
      }

      // Step 5: Update original receipt with link to new receipt
      const { error: linkError } = await supabase
        .from('parent_receipts')
        .update({ replaced_by_receipt_id: newReceipt.id })
        .eq('id', originalReceipt.id);

      if (linkError) throw linkError;

      // Step 6: Create credit note for original receipt
      // Get sequence for credit note
      const { data: cnSeq, error: cnSeqError } = await supabase
        .from('credit_note_sequences')
        .select('last_sequence')
        .eq('branch_id', branchId)
        .eq('year', year)
        .single();

      let creditNoteSeq = 1;
      if (!cnSeqError && cnSeq) {
        creditNoteSeq = cnSeq.last_sequence + 1;
        await supabase
          .from('credit_note_sequences')
          .update({ last_sequence: creditNoteSeq })
          .eq('branch_id', branchId)
          .eq('year', year);
      } else {
        await supabase
          .from('credit_note_sequences')
          .insert({ branch_id: branchId, year, last_sequence: 1 });
      }

      const creditNoteNumber = `KOR/${year}/${String(creditNoteSeq).padStart(4, '0')}`;

      // Calculate totals for credit note (negating original amounts)
      const totalAmount = lineItems.reduce((sum, item) => sum - item.calculated_amount_cents, 0);
      const netTotal = lineItems.reduce((sum, item) => sum - (item.net_amount_cents || item.calculated_amount_cents), 0);
      const vatTotal = lineItems.reduce((sum, item) => sum - (item.vat_amount_cents || 0), 0);

      const { data: creditNote, error: cnError } = await supabase
        .from('credit_notes')
        .insert({
          receipt_id: originalReceipt.id,
          credit_note_number: creditNoteNumber,
          reason: reason || `Korekta rachunku ${originalReceipt.receipt_number}`,
          total_amount_cents: totalAmount,
          net_total_cents: netTotal,
          vat_total_cents: vatTotal,
          status: 'sent',
          created_by: userId,
          sent_at: new Date().toISOString()
        })
        .select()
        .single();

      if (cnError) throw cnError;

      // Add credit note line items
      const creditNoteItems = lineItems.map((item, index) => ({
        credit_note_id: creditNote.id,
        original_line_item_id: item.id,
        correction_type: 'cancel',
        description: `Anulowanie: ${item.description}`,
        original_amount_cents: item.calculated_amount_cents,
        corrected_amount_cents: 0,
        difference_cents: -item.calculated_amount_cents,
        vat_rate: item.vat_rate || 0,
        vat_amount_cents: -(item.vat_amount_cents || 0),
        net_amount_cents: -(item.net_amount_cents || item.calculated_amount_cents),
        quantity: item.quantity || 1,
        display_order: index + 1
      }));

      const { error: cnItemsError } = await supabase
        .from('credit_note_line_items')
        .insert(creditNoteItems);

      if (cnItemsError) throw cnItemsError;

      return { newReceipt, creditNote };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['parent_receipts'] });
      queryClient.invalidateQueries({ queryKey: ['period_receipts'] });
      queryClient.invalidateQueries({ queryKey: ['credit_notes'] });
      toast.success(`Utworzono nowy rachunek ${result.newReceipt.receipt_number}. Możesz go teraz edytować.`);
    },
    onError: (error) => {
      logger.error('Create corrected receipt error', { message: error.message });
      toast.error(`Błąd tworzenia korekty: ${error.message}`);
    }
  });
};

// ============================================================================
// CREDIT NOTES (KOREKTY) HOOKS
// ============================================================================

/**
 * Get credit notes for a receipt
 */
export const useCreditNotes = (receiptId, options = {}) => {
  return useQuery({
    queryKey: ['credit_notes', receiptId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('credit_notes')
        .select(`
          *,
          credit_note_line_items(*)
        `)
        .eq('receipt_id', receiptId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!receiptId,
    ...options
  });
};

/**
 * Get a single credit note with its line items
 */
export const useCreditNote = (creditNoteId, options = {}) => {
  return useQuery({
    queryKey: ['credit_note', creditNoteId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('credit_notes')
        .select(`
          *,
          credit_note_line_items(*),
          parent_receipts(
            id,
            receipt_number,
            child_id,
            children(first_name, last_name)
          )
        `)
        .eq('id', creditNoteId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!creditNoteId,
    ...options
  });
};

/**
 * Create a credit note for a receipt correction
 * This is the main function to use when editing/deleting line items on sent receipts
 */
export const useCreateCreditNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ receiptId, branchId, reason, lineItems, userId }) => {
      // Generate credit note number
      const now = new Date();
      const { data: creditNoteNumber, error: numberError } = await supabase
        .rpc('get_next_credit_note_number', {
          p_branch_id: branchId,
          p_year: now.getFullYear(),
          p_month: now.getMonth() + 1
        });

      if (numberError) throw numberError;

      // Calculate totals from line items
      const totalAmount = lineItems.reduce((sum, item) => sum + item.difference_cents, 0);
      const netTotal = lineItems.reduce((sum, item) => sum + (item.net_amount_cents || item.difference_cents), 0);
      const vatTotal = lineItems.reduce((sum, item) => sum + (item.vat_amount_cents || 0), 0);

      // Create the credit note
      const { data: creditNote, error: creditNoteError } = await supabase
        .from('credit_notes')
        .insert({
          receipt_id: receiptId,
          credit_note_number: creditNoteNumber,
          reason,
          total_amount_cents: totalAmount,
          net_total_cents: netTotal,
          vat_total_cents: vatTotal,
          status: 'draft',
          created_by: userId
        })
        .select()
        .single();

      if (creditNoteError) throw creditNoteError;

      // Add line items to the credit note
      if (lineItems.length > 0) {
        const lineItemsToInsert = lineItems.map((item, index) => ({
          credit_note_id: creditNote.id,
          original_line_item_id: item.original_line_item_id || null,
          correction_type: item.correction_type,
          description: item.description,
          original_amount_cents: item.original_amount_cents || null,
          corrected_amount_cents: item.corrected_amount_cents,
          difference_cents: item.difference_cents,
          vat_rate: item.vat_rate || 0,
          vat_amount_cents: item.vat_amount_cents || 0,
          net_amount_cents: item.net_amount_cents || item.difference_cents,
          quantity: item.quantity || 1,
          notes: item.notes || null,
          display_order: index + 1
        }));

        const { error: lineItemsError } = await supabase
          .from('credit_note_line_items')
          .insert(lineItemsToInsert);

        if (lineItemsError) throw lineItemsError;
      }

      return creditNote;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['credit_notes', variables.receiptId] });
      queryClient.invalidateQueries({ queryKey: ['parent_receipts'] });
      toast.success('Nota korygująca utworzona');
    },
    onError: (error) => {
      logger.error('Create credit note error', { message: error.message });
      toast.error(`Błąd tworzenia noty korygującej: ${error.message}`);
    }
  });
};

/**
 * Send a credit note to parent (update status and timestamps)
 */
export const useSendCreditNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ creditNoteId, sentVia = 'in_app' }) => {
      const { data, error } = await supabase
        .from('credit_notes')
        .update({
          status: 'sent',
          sent_at: new Date().toISOString(),
          sent_via: sentVia
        })
        .eq('id', creditNoteId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit_notes'] });
      toast.success('Nota korygująca wysłana');
    },
    onError: (error) => {
      logger.error('Send credit note error', { message: error.message });
      toast.error(`Błąd wysyłania: ${error.message}`);
    }
  });
};

/**
 * Delete a draft credit note
 */
export const useDeleteCreditNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (creditNoteId) => {
      const { error } = await supabase
        .from('credit_notes')
        .delete()
        .eq('id', creditNoteId)
        .eq('status', 'draft'); // Only allow deleting drafts

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit_notes'] });
      toast.success('Nota korygująca usunięta');
    },
    onError: (error) => {
      logger.error('Delete credit note error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

// ============================================================================
// RECEIPT DISPUTES HOOKS
// ============================================================================

/**
 * Get disputes for a receipt
 */
export const useReceiptDisputes = (receiptId, options = {}) => {
  return useQuery({
    queryKey: ['receipt_disputes', receiptId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('receipt_disputes')
        .select(`
          *,
          parent:app_users!parent_id(id, first_name, last_name),
          resolver:app_users!resolved_by(id, first_name, last_name)
        `)
        .eq('receipt_id', receiptId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!receiptId,
    ...options
  });
};

/**
 * Get all disputes for a branch (for admin view)
 */
export const useBranchDisputes = (branchId, options = {}) => {
  return useQuery({
    queryKey: ['branch_disputes', branchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('receipt_disputes')
        .select(`
          *,
          parent:app_users!parent_id(id, first_name, last_name),
          resolver:app_users!resolved_by(id, first_name, last_name),
          receipt:parent_receipts!receipt_id(
            id,
            receipt_number,
            total_amount_cents,
            child:children(id, first_name, last_name),
            billing_period:billing_periods(id, name, branch_id)
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      // Filter to only this branch's receipts
      return (data || []).filter(d => d.receipt?.billing_period?.branch_id === branchId);
    },
    enabled: !!branchId,
    ...options
  });
};

/**
 * Get disputes for current parent user
 */
export const useMyDisputes = (parentId, options = {}) => {
  return useQuery({
    queryKey: ['my_disputes', parentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('receipt_disputes')
        .select(`
          *,
          receipt:parent_receipts!receipt_id(
            id,
            receipt_number,
            total_amount_cents,
            child:children(id, first_name, last_name),
            billing_period:billing_periods(id, name)
          )
        `)
        .eq('parent_id', parentId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!parentId,
    ...options
  });
};

/**
 * Create a new dispute
 */
export const useCreateDispute = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ receiptId, parentId, reason }) => {
      const { data, error } = await supabase
        .from('receipt_disputes')
        .insert({
          receipt_id: receiptId,
          parent_id: parentId,
          reason: reason.trim(),
          status: 'open'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['receipt_disputes'] });
      queryClient.invalidateQueries({ queryKey: ['my_disputes'] });
      queryClient.invalidateQueries({ queryKey: ['branch_disputes'] });
      queryClient.invalidateQueries({ queryKey: ['period_receipts'] });
      toast.success('Reklamacja została zgłoszona');
    },
    onError: (error) => {
      logger.error('Create dispute error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Resolve or reject a dispute (admin only)
 */
export const useResolveDispute = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ disputeId, status, resolutionNotes, resolvedBy }) => {
      const { data, error } = await supabase
        .from('receipt_disputes')
        .update({
          status,
          resolution_notes: resolutionNotes?.trim() || null,
          resolved_by: resolvedBy,
          resolved_at: new Date().toISOString()
        })
        .eq('id', disputeId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['receipt_disputes'] });
      queryClient.invalidateQueries({ queryKey: ['my_disputes'] });
      queryClient.invalidateQueries({ queryKey: ['branch_disputes'] });
      queryClient.invalidateQueries({ queryKey: ['period_receipts'] });
      const statusText = data.status === 'resolved' ? 'rozpatrzona' : 'odrzucona';
      toast.success(`Reklamacja ${statusText}`);
    },
    onError: (error) => {
      logger.error('Resolve dispute error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Update dispute status (e.g., mark as in_review)
 */
export const useUpdateDisputeStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ disputeId, status }) => {
      const { data, error } = await supabase
        .from('receipt_disputes')
        .update({ status })
        .eq('id', disputeId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['receipt_disputes'] });
      queryClient.invalidateQueries({ queryKey: ['branch_disputes'] });
    },
    onError: (error) => {
      logger.error('Update dispute status error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

// ============================================================================
// DZIENNIK FIELDS (Branch Custom Fields for Daily Updates)
// ============================================================================

/**
 * Get all dziennik fields for a branch
 */
export const useDziennikFields = (branchId, options = {}) => {
  return useQuery({
    queryKey: ['dziennik_fields', branchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('branch_dziennik_fields')
        .select('*')
        .eq('branch_id', branchId)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!branchId,
    ...options
  });
};

/**
 * Get active dziennik fields for a branch (for teachers filling dziennik)
 */
export const useActiveDziennikFields = (branchId, options = {}) => {
  return useQuery({
    queryKey: ['dziennik_fields', branchId, 'active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('branch_dziennik_fields')
        .select('*')
        .eq('branch_id', branchId)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!branchId,
    ...options
  });
};

/**
 * Get number-type dziennik fields for a branch (for cost element event source selection)
 * Only number fields can be used as event sources for per_event cost elements
 */
export const useNumberDziennikFields = (branchId, options = {}) => {
  return useQuery({
    queryKey: ['dziennik_fields', branchId, 'number'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('branch_dziennik_fields')
        .select('*')
        .eq('branch_id', branchId)
        .eq('is_active', true)
        .eq('field_type', 'number')
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!branchId,
    ...options
  });
};

/**
 * Create a new dziennik field
 */
export const useCreateDziennikField = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (field) => {
      const { data, error } = await supabase
        .from('branch_dziennik_fields')
        .insert(field)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dziennik_fields', variables.branch_id] });
      toast.success('Pole dodane');
    },
    onError: (error) => {
      logger.error('Create dziennik field error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Update a dziennik field
 */
export const useUpdateDziennikField = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, branchId, ...updates }) => {
      const { data, error } = await supabase
        .from('branch_dziennik_fields')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { ...data, branchId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dziennik_fields', data.branchId] });
      toast.success('Pole zaktualizowane');
    },
    onError: (error) => {
      logger.error('Update dziennik field error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Delete a dziennik field
 */
export const useDeleteDziennikField = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, branchId }) => {
      const { error } = await supabase
        .from('branch_dziennik_fields')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { id, branchId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dziennik_fields', data.branchId] });
      toast.success('Pole usunięte');
    },
    onError: (error) => {
      logger.error('Delete dziennik field error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Reorder dziennik fields
 */
export const useReorderDziennikFields = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ branchId, orderedIds }) => {
      // Update display_order for each field
      const updates = orderedIds.map((id, index) => ({
        id,
        display_order: index
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('branch_dziennik_fields')
          .update({ display_order: update.display_order })
          .eq('id', update.id);

        if (error) throw error;
      }

      return { branchId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dziennik_fields', data.branchId] });
    },
    onError: (error) => {
      logger.error('Reorder dziennik fields error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

// ============================================================================
// DZIENNIK FIELD VALUES (Values recorded in daily updates)
// ============================================================================

/**
 * Get field values for a daily update
 */
export const useDailyUpdateFieldValues = (dailyUpdateId, options = {}) => {
  return useQuery({
    queryKey: ['daily_update_field_values', dailyUpdateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('daily_update_field_values')
        .select(`
          *,
          field:branch_dziennik_fields(id, name, field_type, unit, is_parent_visible)
        `)
        .eq('daily_update_id', dailyUpdateId);

      if (error) throw error;
      return data || [];
    },
    enabled: !!dailyUpdateId,
    ...options
  });
};

/**
 * Save field values for a daily update (upsert)
 */
export const useSaveDailyUpdateFieldValues = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ dailyUpdateId, values }) => {
      // values is an array of { field_id, number_value?, text_value?, boolean_value?, select_value? }
      const records = values.map(v => ({
        daily_update_id: dailyUpdateId,
        field_id: v.field_id,
        number_value: v.number_value ?? null,
        text_value: v.text_value ?? null,
        boolean_value: v.boolean_value ?? null,
        select_value: v.select_value ?? null
      }));

      const { data, error } = await supabase
        .from('daily_update_field_values')
        .upsert(records, { onConflict: 'daily_update_id,field_id' })
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['daily_update_field_values', variables.dailyUpdateId] });
    },
    onError: (error) => {
      logger.error('Save daily update field values error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Get aggregated field totals for billing (sum of number values in date range)
 */
export const useDziennikFieldTotals = (childId, fieldId, startDate, endDate, options = {}) => {
  return useQuery({
    queryKey: ['dziennik_field_totals', childId, fieldId, startDate, endDate],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_dziennik_field_totals', {
          p_child_id: childId,
          p_field_id: fieldId,
          p_start_date: startDate,
          p_end_date: endDate
        });

      if (error) throw error;
      return data?.[0] || { total_value: 0, entry_count: 0 };
    },
    enabled: !!childId && !!fieldId && !!startDate && !!endDate,
    ...options
  });
};
